import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import {
  ArrowLeft, Loader2, Zap, RefreshCw, Shield, Sparkles, Trophy,
  BookmarkPlus, Archive, CheckCircle2, XCircle, Clock, Trash2,
  ChevronDown, ChevronUp, Check, X, RotateCcw, TrendingUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  bg: "var(--bc-bg, #09090B)", surface: "var(--bc-panel, #18181B)", highlight: "var(--bc-panel-elev, #27272A)",
  border: "var(--bc-border, #27272A)", text: "var(--bc-ink, #FAFAFA)", muted: "var(--bc-sub, #A1A1AA)",
  green: "#22C55E", accent: "var(--bc-accent)", blue: "#3B82F6", red: "#EF4444", yellow: "#F59E0B",
  cyan: "#06B6D4",
};

const SESSION_KEY = "betcracker_session_id";
function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) { sid = crypto.randomUUID?.() || Math.random().toString(36).slice(2); localStorage.setItem(SESSION_KEY, sid); }
  return sid;
}

const TARGETS = [
  { key: "safe",       odds: 2,   label: "SAFE",       sublabel: "Prob. ≥ 62%",  color: C.green,  desc: "Alta certezza, selezioni con probabilità elevata" },
  { key: "aggressive", odds: 3.5, label: "AGGRESSIVA", sublabel: "Prob. ≥ 52%",  color: C.yellow, desc: "Bilanciata, maggior numero di selezioni" },
  { key: "risky",      odds: 6,   label: "RISCHIOSA",  sublabel: "Prob. ≥ 40%",  color: C.red,    desc: "Alto rischio, include selezioni meno probabili" },
];

const STATUS_CONFIG = {
  pending:  { label: "IN CORSO",  color: C.yellow,  Icon: Clock },
  won:      { label: "VINTA",     color: C.green,   Icon: CheckCircle2 },
  lost:     { label: "PERSA",     color: C.red,     Icon: XCircle },
};

function LeagueBadge({ flag, name }) {
  return (
    <div className="flex items-center gap-1">
      {flag && <img src={flag} alt="" className="w-3 h-2 object-cover" onError={e => e.target.style.display="none"} />}
      <span style={{ color: C.muted, fontSize: "0.58rem" }} className="font-data uppercase tracking-wide">{name}</span>
    </div>
  );
}

function SlipRow({ s, i, result, onOutcome, readOnly }) {
  const outcome = result?.outcome || "pending";
  const outColor = outcome === "won" ? C.green : outcome === "lost" ? C.red : C.muted;
  return (
    <div className="flex items-center gap-3 px-4 py-3.5" data-testid={`slip-item-${i}`}
      style={{ borderBottom: `1px solid ${C.highlight}` }}>
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 font-data text-xs font-bold"
        style={{ background: C.highlight, color: C.muted }}>{i + 1}</div>

      <div className="flex-1 min-w-0">
        <LeagueBadge flag={s.league_flag} name={s.league} />
        <div className="flex items-center gap-1.5 mt-0.5">
          {s.home_logo && <img src={s.home_logo} alt="" className="w-4 h-4 object-contain" onError={e => e.target.style.display="none"} />}
          <span className="font-heading font-bold text-xs uppercase truncate" style={{ color: C.text }}>{s.home}</span>
          <span className="font-data text-xs" style={{ color: C.muted }}>vs</span>
          {s.away_logo && <img src={s.away_logo} alt="" className="w-4 h-4 object-contain" onError={e => e.target.style.display="none"} />}
          <span className="font-heading font-bold text-xs uppercase truncate" style={{ color: C.text }}>{s.away}</span>
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <div className="flex items-center gap-1">
          {s.is_target_kitano && <Zap size={9} style={{ color: C.yellow }} fill={C.yellow} />}
          {s.type === "combo" && (
            <span className="font-data text-[9px] px-1 py-0.5 uppercase tracking-wide"
              style={{ background: "rgba(167,139,250,0.15)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.3)" }}>
              COMBO
            </span>
          )}
          <span className="font-data text-xs font-medium" style={{ color: s.type === "combo" ? "#A78BFA" : C.text, maxWidth: 130 }}>{s.bet}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {s.book_odds && (
            <span className="font-data text-xs tabular-nums font-bold" style={{ color: C.cyan }}>
              {s.book_odds.toFixed(2)}
            </span>
          )}
          {s.has_value && (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm flex-shrink-0" style={{ background: C.accent }}>
              <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 4.5l2 2L7.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
          )}
          <span className="font-data text-xs font-medium" style={{ color: s.prob > 65 ? C.green : s.prob > 52 ? C.yellow : C.muted }}>{s.prob}%</span>
        </div>

        {/* Outcome buttons + auto-detected score */}
        {!readOnly && (
          <div className="flex flex-col items-end gap-1">
            {result?.score && (
              <span className="font-data text-xs font-bold px-1.5 py-0.5"
                style={{ background: `${outColor}15`, color: outColor, border: `1px solid ${outColor}30` }}>
                {result.score}
              </span>
            )}
            <div className="flex items-center gap-1">
              <button onClick={() => onOutcome(s.fixture_id, "won")}
                className="w-5 h-5 flex items-center justify-center transition-all"
                style={{ background: outcome === "won" ? C.green : C.highlight, border: `1px solid ${outcome === "won" ? C.green : C.border}` }}
                data-testid={`outcome-won-${s.fixture_id}`} title="Vincente">
                <Check size={10} style={{ color: outcome === "won" ? "#000" : C.muted }} />
              </button>
              <button onClick={() => onOutcome(s.fixture_id, "lost")}
                className="w-5 h-5 flex items-center justify-center transition-all"
                style={{ background: outcome === "lost" ? C.red : C.highlight, border: `1px solid ${outcome === "lost" ? C.red : C.border}` }}
                data-testid={`outcome-lost-${s.fixture_id}`} title="Perdente">
                <X size={10} style={{ color: outcome === "lost" ? "#fff" : C.muted }} />
              </button>
              <button onClick={() => onOutcome(s.fixture_id, "pending")}
                className="w-5 h-5 flex items-center justify-center transition-all"
                style={{ background: outcome === "pending" ? C.yellow : C.highlight, border: `1px solid ${outcome === "pending" ? C.yellow : C.border}` }}
                data-testid={`outcome-pending-${s.fixture_id}`} title="Reimposta">
                <RotateCcw size={9} style={{ color: outcome === "pending" ? "#000" : C.muted }} />
              </button>
            </div>
          </div>
        )}
        {readOnly && (
          <div className="flex flex-col items-end gap-0.5">
            {result?.score && (
              <span className="font-data text-xs font-bold px-1.5 py-0.5"
                style={{ background: `${outColor}15`, color: outColor, border: `1px solid ${outColor}30` }}>
                {result.score}
              </span>
            )}
            {outcome !== "pending" && (
              <span className="font-data text-xs font-bold" style={{ color: outColor }}>
                {outcome === "won" ? "VINTA" : "PERSA"}
              </span>
            )}
            {outcome === "pending" && (
              <Clock size={11} style={{ color: C.muted }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SlipCard({ slip, combinedOdds, combinedProb, targetColor, onSave, saving }) {
  if (!slip.length) return null;
  return (
    <div style={{ background: C.surface, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderTop: `3px solid ${targetColor}` }}
      className="animate-fade-in" data-testid="slip-card">
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: targetColor }} />
          <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>SCHEDINA PROPOSTA</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-data text-xs" style={{ color: C.muted }}>Prob. cumulativa</span>
          <span className="font-data text-sm font-bold"
            style={{ color: combinedProb > 40 ? C.green : combinedProb > 20 ? C.yellow : C.red }}>{combinedProb}%</span>
        </div>
      </div>

      {slip.map((s, i) => (
        <SlipRow key={`${s.fixture_id}-${s.bet}`} s={s} i={i} readOnly />
      ))}

      <div className="flex items-center justify-between px-4 py-3"
        style={{ background: C.highlight, borderTop: `1px solid ${C.border}` }}>
        <div>
          <span className="font-heading font-bold text-sm uppercase tracking-wider" style={{ color: C.muted }}>PROBABILITÀ CUMULATA</span>
          <p className="font-data text-xs mt-0.5" style={{ color: C.muted }}>Prodotto delle probabilità del modello</p>
        </div>
        <span className="font-data text-2xl font-bold"
          style={{ color: combinedProb > 40 ? C.green : combinedProb > 20 ? C.yellow : C.red }}>
          {combinedProb}%
        </span>
      </div>

      {/* Save button */}
      {onSave && (
        <div className="px-4 py-3" style={{ borderTop: `1px solid ${C.border}` }}>
          <button data-testid="save-multipla-btn" onClick={onSave} disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-2.5 transition-all disabled:opacity-50"
            style={{ background: "rgba(34,197,94,0.12)", border: `1px solid ${C.green}`, color: C.green }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <BookmarkPlus size={14} />}
            <span className="font-heading font-bold text-sm uppercase tracking-wider">
              {saving ? "Salvataggio..." : "Salva questa schedina"}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function SavedMultiplaCard({ m, onDelete, onUpdateOutcome }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.pending;
  const target = TARGETS.find(t => t.key === m.target) || TARGETS[0];
  const dateLabel = m.date ? new Date(m.date + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "";
  const savedLabel = m.saved_at ? new Date(m.saved_at).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `4px solid ${cfg.color}` }}
      data-testid={`saved-multipla-${m.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <cfg.Icon size={16} style={{ color: cfg.color }} />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-sm uppercase tracking-wider" style={{ color: C.text }}>
                {target.label}
              </span>
              <span className="font-data text-xs px-1.5 py-0.5 uppercase"
                style={{ background: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                {cfg.label}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-data text-xs" style={{ color: C.muted }}>{dateLabel}</span>
              <span style={{ color: C.border }}>·</span>
              <span className="font-data text-xs" style={{ color: C.muted }}>Salvata {savedLabel}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="font-data text-xl font-bold" style={{ color: target.color }}>{m.combined_prob}%</span>
            <p className="font-data text-xs" style={{ color: C.muted }}>prob. cumulata</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setExpanded(v => !v)}
              className="w-7 h-7 flex items-center justify-center transition-all hover:opacity-70"
              style={{ background: C.highlight, border: `1px solid ${C.border}` }}
              data-testid={`expand-multipla-${m.id}`}>
              {expanded ? <ChevronUp size={12} style={{ color: C.muted }} /> : <ChevronDown size={12} style={{ color: C.muted }} />}
            </button>
            <button onClick={() => onDelete(m.id)}
              className="w-7 h-7 flex items-center justify-center transition-all hover:opacity-70"
              style={{ background: C.highlight, border: `1px solid ${C.border}` }}
              data-testid={`delete-multipla-${m.id}`}>
              <Trash2 size={12} style={{ color: C.red }} />
            </button>
          </div>
        </div>
      </div>

      {/* Slip details */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          {m.slip.map((s, i) => {
            const res = m.results?.find(r => r.fixture_id === s.fixture_id);
            return (
              <SlipRow key={`${s.fixture_id}-${s.bet}`} s={s} i={i} result={res}
                onOutcome={(fid, outcome) => onUpdateOutcome(m.id, fid, outcome)} />
            );
          })}
          <div className="px-4 py-3 flex items-center justify-between"
            style={{ background: C.highlight, borderTop: `1px solid ${C.border}` }}>
            <span className="font-heading font-bold text-xs uppercase" style={{ color: C.muted }}>
              Segna i risultati usando i pulsanti ✓ / ✗ su ogni selezione
            </span>
            <span className="font-data text-xl font-bold" style={{ color: target.color }}>{m.combined_prob}% cumulata</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MultiplaGolosaPage() {
  const [tab, setTab] = useState("genera");
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [slipData, setSlipData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState([]);
  const [savedMultiplas, setSavedMultiplas] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [autoUpdating, setAutoUpdating] = useState(false);
  const [autoUpdateMsg, setAutoUpdateMsg] = useState("");
  const navigate = useNavigate();
  const sessionId = getSessionId();

  const loadArchive = useCallback(async () => {
    setArchiveLoading(true);
    try {
      const res = await axios.get(`${API}/saved-multipla/${sessionId}`);
      setSavedMultiplas(res.data.multiplas || []);
    } catch (e) { console.error("[Multipla] Failed to load archive:", e); }
    setArchiveLoading(false);
  }, [sessionId]);

  useEffect(() => { if (tab === "archivio") loadArchive(); }, [tab, loadArchive]);

  const generate = useCallback(async (target) => {
    setLoading(true); setSaved(false);
    try {
      const res = await axios.post(`${API}/multipla-golosa?target_odds=${target.odds}`);
      setSlipData(res.data);
      setHistory(prev => [res.data, ...prev.slice(0, 4)]);
    } catch (e) { console.error("[Multipla] Failed to generate:", e); }
    setLoading(false);
  }, []);

  const handleSelect = (target) => {
    setSelectedTarget(target); setSlipData(null); setHistory([]); generate(target);
  };

  const handleSave = useCallback(async () => {
    if (!slipData || !selectedTarget) return;
    setSaving(true);
    try {
      await axios.post(`${API}/saved-multipla`, {
        session_id: sessionId,
        slip: slipData.slip,
        combined_odds: slipData.combined_odds,
        combined_prob: slipData.combined_prob,
        date: slipData.date,
        target: selectedTarget.key,
      });
      setSaved(true);
    } catch (e) { console.error("[Multipla] Failed to save:", e); }
    setSaving(false);
  }, [slipData, selectedTarget, sessionId]);

  const handleAutoUpdate = useCallback(async () => {
    setAutoUpdating(true); setAutoUpdateMsg("");
    try {
      const res = await axios.post(`${API}/saved-multipla/auto-update/${sessionId}`);
      const { updated, message, checked_fixtures } = res.data;
      setAutoUpdateMsg(updated > 0
        ? `${message} (${checked_fixtures} partite controllate)`
        : `Nessun aggiornamento disponibile (${checked_fixtures ?? 0} partite controllate)`);
      if (updated > 0) await loadArchive();
    } catch (e) {
      console.error("[Multipla] Auto-update failed:", e);
      setAutoUpdateMsg("Errore durante l'aggiornamento automatico.");
    }
    setAutoUpdating(false);
  }, [sessionId, loadArchive]);

  const handleDelete = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/saved-multipla/${sessionId}/${id}`);
      setSavedMultiplas(prev => prev.filter(m => m.id !== id));
    } catch (e) { console.error("[Multipla] Failed to delete:", e); }
  }, [sessionId]);

  const handleUpdateOutcome = useCallback(async (multiplaId, fixtureId, outcome) => {
    try {
      const res = await axios.patch(`${API}/saved-multipla/${sessionId}/${multiplaId}`, {
        fixture_id: fixtureId, outcome
      });
      setSavedMultiplas(prev => prev.map(m =>
        m.id === multiplaId
          ? { ...m, status: res.data.status, results: res.data.results }
          : m
      ));
    } catch (e) { console.error("[Multipla] Failed to update outcome:", e); }
  }, [sessionId]);

  return (
    <div className="min-h-screen" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button data-testid="back-btn" onClick={() => navigate("/")}
          className="flex items-center gap-2 mb-6 transition-opacity hover:opacity-70">
          <ArrowLeft size={16} style={{ color: C.muted }} />
          <span className="font-heading text-sm uppercase tracking-widest" style={{ color: C.muted }}>Torna alla Home</span>
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Trophy size={22} style={{ color: C.yellow }} />
          <h1 className="font-heading font-bold text-2xl uppercase tracking-wider" style={{ color: C.text }}>MULTIPLA GOLOSA</h1>
        </div>
        <p className="font-data text-sm mb-6" style={{ color: C.muted }}>
          Schedine generate dai campionati principali, con selezioni ad alta probabilità e convergenza dei modelli
        </p>

        {/* Tabs */}
        <div className="flex gap-0 mb-8" style={{ borderBottom: `1px solid ${C.border}` }}>
          {[
            { key: "genera", label: "GENERA", Icon: Sparkles },
            { key: "archivio", label: "ARCHIVIO", Icon: Archive },
          ].map(({ key, label, Icon }) => (
            <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
              className="flex items-center gap-2 px-5 py-3 transition-all"
              style={{
                borderBottom: tab === key ? `2px solid ${C.accent}` : "2px solid transparent",
                color: tab === key ? C.text : C.muted,
                marginBottom: -1,
              }}>
              <Icon size={14} />
              <span className="font-heading font-bold text-sm uppercase tracking-wider">{label}</span>
              {key === "archivio" && savedMultiplas.length > 0 && (
                <span className="font-data text-xs px-1.5 py-0.5"
                  style={{ background: C.highlight, color: C.muted }}>{savedMultiplas.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* GENERA TAB */}
        {tab === "genera" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="target-selector">
              {TARGETS.map((t) => (
                <button key={t.key} data-testid={`target-${t.key}`} onClick={() => handleSelect(t)}
                  className="p-5 text-left transition-all duration-200"
                  style={{
                    background: selectedTarget?.key === t.key ? "var(--bc-panel-elev, rgba(255,255,255,0.04))" : C.surface,
                    border: `2px solid ${selectedTarget?.key === t.key ? t.color : C.border}`,
                    borderTop: `4px solid ${t.color}`,
                  }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading font-bold text-lg uppercase tracking-wider" style={{ color: t.color }}>{t.label}</span>
                    <Shield size={18} style={{ color: t.color }} />
                  </div>
                  <p className="font-data text-xl font-bold mb-1" style={{ color: C.text }}>{t.sublabel}</p>
                  <p className="font-data text-xs" style={{ color: C.muted }}>{t.desc}</p>
                </button>
              ))}
            </div>

            {selectedTarget && (
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <button data-testid="regenerate-btn" onClick={() => generate(selectedTarget)}
                    disabled={loading}
                    className="flex items-center gap-2 px-5 py-2.5 transition-all disabled:opacity-50"
                    style={{ background: selectedTarget.color, color: "#09090B" }}>
                    {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                    <span className="font-heading font-bold text-sm uppercase tracking-wider">
                      {loading ? "Generazione..." : "Genera Nuova Proposta"}
                    </span>
                  </button>
                  {history.length > 1 && (
                    <span className="font-data text-xs" style={{ color: C.muted }}>{history.length} proposte generate</span>
                  )}
                  {saved && (
                    <span className="flex items-center gap-1.5 font-data text-xs" style={{ color: C.green }}>
                      <CheckCircle2 size={13} /> Schedina salvata nell'archivio
                    </span>
                  )}
                </div>

                {slipData && slipData.slip.length > 0 && (
                  <SlipCard
                    slip={slipData.slip}
                    combinedOdds={slipData.combined_odds}
                    combinedProb={slipData.combined_prob}
                    targetColor={selectedTarget.color}
                    onSave={saved ? null : handleSave}
                    saving={saving}
                  />
                )}

                {slipData && slipData.slip.length === 0 && !loading && (
                  <div className="text-center py-12" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                    <Trophy size={32} style={{ color: C.muted }} className="mx-auto mb-3 opacity-30" />
                    <p className="font-heading text-sm" style={{ color: C.muted }}>Nessuna partita disponibile per questa fascia di quota</p>
                    <p className="font-data text-xs mt-1" style={{ color: C.muted }}>
                      Le schedine usano solo partite da giocare con alta convergenza statistica
                    </p>
                  </div>
                )}

                {history.length > 1 && (
                  <div>
                    <p className="font-heading font-bold text-xs uppercase tracking-widest mb-3" style={{ color: C.muted }}>PROPOSTE PRECEDENTI</p>
                    <div className="space-y-4">
                      {history.slice(1).map((h, i) => (
                        <div key={i} className="opacity-60 hover:opacity-100 transition-opacity">
                          <SlipCard slip={h.slip} combinedOdds={h.combined_odds}
                            combinedProb={h.combined_prob} targetColor={selectedTarget.color} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!selectedTarget && (
              <div className="text-center py-16" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <Trophy size={40} style={{ color: C.yellow }} className="mx-auto mb-4 opacity-30" />
                <p className="font-heading text-lg" style={{ color: C.muted }}>Seleziona un tipo di schedina per iniziare</p>
                <p className="font-data text-sm mt-2" style={{ color: C.muted }}>
                  Le selezioni vengono scelte in ordine di priorità: campionati principali prima, alta probabilità, alta convergenza
                </p>
              </div>
            )}
          </>
        )}

        {/* ARCHIVIO TAB */}
        {tab === "archivio" && (
          <div>
            {/* Auto-update bar */}
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <button data-testid="auto-update-btn" onClick={handleAutoUpdate} disabled={autoUpdating}
                className="flex items-center gap-2 px-4 py-2.5 transition-all disabled:opacity-50"
                style={{ background: "rgba(6,182,212,0.10)", border: `1px solid ${C.cyan}`, color: C.cyan }}>
                <RefreshCw size={14} className={autoUpdating ? "animate-spin" : ""} />
                <span className="font-heading font-bold text-sm uppercase tracking-wider">
                  {autoUpdating ? "Aggiornamento in corso..." : "Aggiorna Risultati"}
                </span>
              </button>
              {autoUpdateMsg && (
                <span className="font-data text-xs flex items-center gap-1.5"
                  style={{ color: autoUpdateMsg.includes("Nessun") ? C.muted : C.green }}>
                  <CheckCircle2 size={13} />
                  {autoUpdateMsg}
                </span>
              )}
            </div>
            {archiveLoading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin" style={{ color: C.muted }} />
              </div>
            )}

            {!archiveLoading && savedMultiplas.length === 0 && (
              <div className="text-center py-16" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <Archive size={40} style={{ color: C.muted }} className="mx-auto mb-4 opacity-30" />
                <p className="font-heading text-lg" style={{ color: C.muted }}>Nessuna schedina salvata</p>
                <p className="font-data text-sm mt-2" style={{ color: C.muted }}>
                  Genera una schedina e clicca "Salva questa schedina" per iniziare il monitoraggio
                </p>
                <button onClick={() => setTab("genera")} className="mt-4 px-4 py-2 font-heading font-bold text-sm uppercase tracking-wider"
                  style={{ background: C.accent, color: "#09090B" }} data-testid="go-to-genera-btn">
                  Vai a Genera
                </button>
              </div>
            )}

            {!archiveLoading && savedMultiplas.length > 0 && (
              <div className="space-y-4">
                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { label: "TOTALI", val: savedMultiplas.length, color: C.muted },
                    { label: "VINTE", val: savedMultiplas.filter(m => m.status === "won").length, color: C.green },
                    { label: "PERSE", val: savedMultiplas.filter(m => m.status === "lost").length, color: C.red },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="text-center py-4"
                      style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                      <p className="font-data text-3xl font-bold" style={{ color }}>{val}</p>
                      <p className="font-data text-xs uppercase tracking-widest mt-1" style={{ color: C.muted }}>{label}</p>
                    </div>
                  ))}
                </div>

                {savedMultiplas.map(m => (
                  <SavedMultiplaCard key={m.id} m={m}
                    onDelete={handleDelete} onUpdateOutcome={handleUpdateOutcome} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
